import { ReactNode, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

interface ParallaxSectionProps {
  children: ReactNode;
  speed?: number;
  direction?: "up" | "down";
  className?: string;
  /** Set false for heavy sections (canvas, video, complex grids) to skip parallax entirely on slow devices */
  disabled?: boolean;
}

// Detect low-end / preference for reduced motion once at module level
const prefersReducedMotion =
  typeof window !== "undefined"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

export default function ParallaxSection({
  children,
  speed = 0.5,
  direction = "up",
  className = "",
  disabled = false,
}: ParallaxSectionProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef   = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Skip parallax for reduced-motion users or explicitly disabled sections
    if (disabled || prefersReducedMotion) return;

    const wrapper = wrapperRef.current;
    const inner   = innerRef.current;
    if (!wrapper || !inner) return;

    const yMove = direction === "up" ? -(speed * 100) : (speed * 100);

    const ctx = gsap.context(() => {
      gsap.fromTo(
        inner,
        { y: -yMove },
        {
          y: yMove,
          ease: "none",
          scrollTrigger: {
            trigger: wrapper,
            start: "top bottom",
            end: "bottom top",
            // KEY FIX: scrub: true (or a very small number like 0.3) instead of 1.
            // scrub:1 means GSAP lags 1 full second behind scroll — on heavy
            // sections this looks like a rubber-band glitch. scrub:true is
            // frame-perfect (no lag). Use 0.3 for a subtle smooth feel.
            scrub: 0.3,
            invalidateOnRefresh: true,
          },
        }
      );
    }, wrapper);

    return () => ctx.revert();
  }, [speed, direction, disabled]);

  return (
    /*
      REMOVED overflow-hidden from the outer wrapper.
      Each overflow-hidden creates a stacking context that forces the browser
      to recalculate clip regions every scroll frame — multiplied across 4
      sections this causes visible jank. Only add it back if content visually
      bleeds outside the section (check each one individually).
    */
    <div ref={wrapperRef} className={className}>
      <div
        ref={innerRef}
        style={{
          // willChange only when parallax is actually active.
          // Always-on willChange on every section forces GPU layers for all of
          // them simultaneously, which is worse than no willChange at all.
          willChange: disabled || prefersReducedMotion ? "auto" : "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
}